// Components
import Box from "@mui/material/Box";
import {
  BoldCaption,
  BoldHeadFront,
  HEAD_CENTRE_X,
  INK,
  INK_2,
  WARM,
} from "./boldHead";

// How much larger than life the tilt is drawn at. A real cyclodeviation is a couple of degrees,
// which is invisible at this size; the number printed beside the figure is the measured one.
const EXAGGERATION = 6;

const EYE_Y = 52;
const EYE_OFFSET = 13;
// How far the reference and measured lines run either side of the eye. Both reach well past the
// head so the gap between them can be seen at all.
const INNER = 11;
const OUTER = 54;
const ARC_RADIUS = 27;

export type TorsionScopeType = "both" | "left" | "right";

type TorsionFigureType = {
  degrees: number;
  // "Excyclo" or "Incyclo", straight off the payload.
  direction: string;
  scope: TorsionScopeType;
  caption: string;
};

/**
 * Eye tilt, drawn on the eyes rather than on an abstract dial.
 *
 * Each measured eye carries a dashed upright reference and a solid line where the patient set
 * the image, with the angle between them marked. Excyclo turns the outer end downwards.
 *
 * The head faces the reader, so this figure is a MIRROR: the patient's left eye is on the
 * reader's right. See `BoldHeadFront`.
 */
const TorsionFigure = ({
  degrees,
  direction,
  scope,
  caption,
}: TorsionFigureType) => {
  const excyclo = /^ex/i.test(direction);
  const angle = (degrees * EXAGGERATION * (excyclo ? 1 : -1) * Math.PI) / 180;
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);

  return (
    <Box
      component="svg"
      role="img"
      data-testid="synoptophore-torsion-figure"
      aria-label={caption}
      viewBox="-16 18 152 112"
      sx={{ display: "block", width: "100%", mx: "auto" }}
    >
      <BoldHeadFront />

      {[-1, 1].map((side) => {
        // `side` is where the eye sits on screen. Mirrored, so the reader's RIGHT (side 1) is
        // the patient's LEFT eye.
        const measured =
          scope === "both" ||
          (scope === "left" && side === 1) ||
          (scope === "right" && side === -1);
        const eyeX = HEAD_CENTRE_X + side * EYE_OFFSET;

        return (
          <g key={side}>
            {measured && (
              <>
                <line
                  x1={eyeX - side * INNER}
                  y1={EYE_Y}
                  x2={eyeX + side * OUTER}
                  y2={EYE_Y}
                  stroke={INK_2}
                  strokeOpacity={0.45}
                  strokeWidth={1.4}
                  strokeDasharray="5 4"
                />
                <line
                  x1={eyeX - side * INNER * cos}
                  y1={EYE_Y - INNER * sin}
                  x2={eyeX + side * OUTER * cos}
                  y2={EYE_Y + OUTER * sin}
                  stroke={WARM}
                  strokeWidth={2.6}
                  strokeLinecap="round"
                />
                <path
                  d={`M${eyeX + side * ARC_RADIUS} ${EYE_Y} A${ARC_RADIUS} ${ARC_RADIUS} 0 0 ${
                    side > 0 ? 1 : 0
                  } ${eyeX + side * ARC_RADIUS * cos} ${EYE_Y + ARC_RADIUS * sin}`}
                  fill="none"
                  stroke={INK_2}
                  strokeWidth={1.3}
                />
                <text
                  x={eyeX + side * (ARC_RADIUS + 2)}
                  y={EYE_Y - 7}
                  fontSize="10"
                  fontWeight={600}
                  fill={INK}
                  textAnchor="middle"
                >
                  {`${degrees.toFixed(1)}°`}
                </text>
              </>
            )}

            <ellipse
              cx={eyeX}
              cy={EYE_Y}
              rx={9.5}
              ry={6.8}
              fill="#FFFFFF"
              stroke={measured ? WARM : "#6B7488"}
              strokeWidth={measured ? 2.4 : 1.6}
            />
            <circle cx={eyeX} cy={EYE_Y} r={3.1} fill={INK} />
          </g>
        );
      })}

      <BoldCaption text={caption} />
    </Box>
  );
};

export default TorsionFigure;
